import React, { useEffect, useRef, useState } from "react";
import AceEditor from "react-ace";
import { editorConfig } from "../utils/aceConfig";
import { HTMLHint } from "htmlhint";
import ace from "ace-builds/src-noconflict/ace";
import "ace-builds/src-noconflict/ext-beautify";
import "ace-builds/src-noconflict/ext-emmet";       // Emmet support
import "ace-builds/src-noconflict/ext-language_tools"; // autocomplete infra
import "ace-builds/src-noconflict/snippets/html";

export default function HtmlTreeEditor({ html, onChange }) {
  const [localHtml, setLocalHtml] = useState(html);
  const editorRef = useRef(null);
  const debounceRef = useRef(null);
  const hasBeautifiedOnMount = useRef(false);

  // Beautify ONCE on initial mount only — not on every prop change
  useEffect(() => {
    if (hasBeautifiedOnMount.current) return;
    hasBeautifiedOnMount.current = true;

    if (html) {
      const beautify = ace.require("ace/ext/beautify");
      const session = ace.createEditSession(html, "ace/mode/html");
      beautify.beautify(session);
      const beautified = session.getValue();
      setLocalHtml(beautified);
      onChange(beautified);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const validateHtml = (editor, code) => {
    const results = HTMLHint.verify(code);
    const annotations = results.map((err) => ({
      row: (err.line || 1) - 1,
      column: (err.col || 1) - 1,
      text: err.message,
      type: "error"
    }));
    editor.getSession().setAnnotations(annotations);
  };

  const debouncedValidate = (editor) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      validateHtml(editor, editor.getValue());
    }, 400); // wait for typing to pause
  };

  // Manual "Format" trigger — call this from a toolbar button instead of on every render
  const handleFormat = () => {
    if (editorRef.current) {
      const beautify = ace.require("ace/ext/beautify");
      beautify.beautify(editorRef.current.editor.getSession());
    }
  };

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  return (
    <>
      <button type="button" onClick={handleFormat} className="btn btn-sm btn-secondary mb-2"> 
        Format Code
      </button>
      <AceEditor
        ref={editorRef}
        mode={editorConfig.mode}
        theme={editorConfig.theme}
        name="html-editor"
        width="100%"
        height="80vh"
        value={localHtml}
        setOptions={{
          ...editorConfig.setOptions,
          enableBasicAutocompletion: true,
          enableLiveAutocompletion: true,
          enableSnippets: true,
          enableEmmet: true // works once ext-emmet is loaded above
        }}
        onChange={(val) => {
          setLocalHtml(val);
          onChange(val);
        }}
        fontSize={17}
        lineHeight={20}
        showPrintMargin={true}
        showGutter={true}
        highlightActiveLine={true}
        onLoad={(editor) => {
          validateHtml(editor, localHtml);
          editor.getSession().on("change", () => {
            debouncedValidate(editor);
          });
        }}
        editorProps={{ $blockScrolling: true }}
      />
    </>
  );
}